import { useNavigate } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Star, Users, Settings, Globe, Lock } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "@/hooks/use-toast";

interface LeagueCardProps {
  league: {
    id: string;
    name: string;
    emoji: string | null;
    member_count: number | null;
    is_public: boolean | null;
  };
  isFavourite: boolean;
  isOwner: boolean;
  userId: string;
  membershipId: string;
  rank?: number | null;
  totalMembers?: number;
  hasResults?: boolean;
}

const PODIUM_COLORS: Record<number, { text: string; border: string }> = {
  1: { text: "#FFD700", border: "rgba(255, 215, 0, 0.45)" },
  2: { text: "#C0C0C0", border: "rgba(192, 192, 192, 0.45)" },
  3: { text: "#CD7F32", border: "rgba(205, 127, 50, 0.45)" },
};

const LeagueCard = ({ league, isFavourite, isOwner, userId, membershipId, rank, totalMembers, hasResults = true }: LeagueCardProps) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const toggleFavourite = async (e: React.MouseEvent) => {
    e.stopPropagation();
    const { error } = await supabase
      .from("league_members")
      .update({ is_favourite: !isFavourite })
      .eq("id", membershipId);
    if (error) {
      toast({ title: "Failed to update", description: error.message, variant: "destructive" });
    } else {
      queryClient.invalidateQueries({ queryKey: ["my-leagues"] });
    }
  };

  const showRank = hasResults && rank != null && rank > 0;
  const podium = showRank && rank <= 3 ? PODIUM_COLORS[rank] : null;

  return (
    <Card
      className="cursor-pointer transition-all hover:shadow-md hover:scale-[1.01]"
      style={podium ? { borderColor: podium.border, borderWidth: 2 } : undefined}
      onClick={() => navigate(`/leagues/${league.id}`)}
    >
      <CardContent className="flex items-center gap-3 p-4">
        <span className="text-3xl flex-shrink-0">{league.emoji || "🏆"}</span>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5">
            <p className="font-semibold truncate">{league.name}</p>
            <button onClick={toggleFavourite} className="flex-shrink-0">
              <Star
                className={`h-4 w-4 transition-colors ${isFavourite ? "fill-yellow-400 text-yellow-400" : "text-muted-foreground/40 hover:text-yellow-400"}`}
              />
            </button>
          </div>
          <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground mt-0.5">
            <span className="flex items-center gap-1"><Users className="h-3 w-3" /> {league.member_count ?? 0}</span>
            <span className="flex items-center gap-1">
              {league.is_public ? <><Globe className="h-3 w-3" /> Public</> : <><Lock className="h-3 w-3" /> Private</>}
            </span>
            {showRank ? (
              <span className="font-bold" style={podium ? { color: podium.text } : undefined}>P{rank}</span>
            ) : (
              <span className="font-bold text-muted-foreground">—</span>
            )}
          </div>
        </div>
        {isOwner && (
          <Button
            size="sm"
            variant="ghost"
            className="h-8 px-2 text-muted-foreground"
            onClick={(e) => { e.stopPropagation(); navigate(`/leagues/${league.id}`); }}
          >
            <Settings className="h-4 w-4" />
          </Button>
        )}
      </CardContent>
    </Card>
  );
};

export default LeagueCard;
